/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import DocIcon from '../../components/shared/DocIcon'
import ValidDocIndicator from '../../components/shared/ValidDocIndicator'
import { FsiGridItem } from './FsiGridItem'
import { buildUrlWithCSelections } from '~/utils/navigation'
import { DocumentsViewType } from './types'
import useDocumentsListService from '../../hooks/useDocumentsListService'
import { DOCUMENTS_VIEW_TYPE } from './types'
import { DocumentActionMenu } from '../../components/home/sections/recent-docs/DocumentActionMenu'
import WorkflowDetailsModal from '~/components/modals/WorkflowDetailsModal'
import { useModal } from '~/contexts/ModalContext'
import TableMenu from '../../components/home/sections/upcoming-periodic-review/TableMenu'

interface PeriodicReviewTableProps {
  viewType: DocumentsViewType
  initialPage?: number
  filterShown?: boolean
}

export const PeriodicReviewTable: React.FC<PeriodicReviewTableProps> = ({ viewType, initialPage }) => {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const { getFsiNameTooltip } = useDocumentsListService()
  const { showModal } = useModal()
  const {
    service: {
      filter: { translate }
    }
  } = context

  useEffect(() => {
    // Listen for table update events via window event bus
    const handleUpdateTable = () => {
      tableData.refreshTable()
    }

    window.addEventListener('bctable:updateTable', handleUpdateTable)
    window.addEventListener('updateFiles', handleUpdateTable)

    return () => {
      window.removeEventListener('bctable:updateTable', handleUpdateTable)
      window.removeEventListener('updateFiles', handleUpdateTable)
    }
  }, [tableData])

  useEffect(() => {
    // Polling for periodic updates - simplified for now
    const intervalId = setInterval(() => {
      tableData.refreshTable()
    }, 30000) // Poll every 30 seconds

    return () => {
      clearInterval(intervalId)
    }
  }, [tableData])

  useEffect(() => {
    if ([DOCUMENTS_VIEW_TYPE.periodicReviewUpcoming].includes(viewType as any)) {
      tableData.refreshTable()
    }
  }, [])

  // Use React Router navigation for show-file route
  const navigate = useNavigate()
  const handleRowClick = (fsi: any) => {
    navigate(`/show-file/${fsi.id}`)
  }

  const formatDate = (dateValue: number | string | undefined) => {
    if (!dateValue) return '-'
    const date = new Date(dateValue)
    return date.toLocaleDateString()
  }

  return (
    <RcmTable
      className="chita-table fsiTable"
      onRowEnter={handleRowClick}
      simplePaginate={false}
      GridItemView={(props) => <FsiGridItem {...props} viewType={viewType} />}
      rowKey="id"
      HeadRow={
        <TableHeadRow>
          <TableHeadCell id="0abd7383-1ac6-4f08-a239-ae8306c06958">{translate('NAME')}</TableHeadCell>
          <TableHeadCell className="narrow" id="0e5d5299-31aa-4925-88f5-1764a9edac76">
            {translate('CHANGE_REQUEST')}
          </TableHeadCell>
          <TableHeadCell className="narrow" id="4dcdc797-82a1-4a53-ba41-5bfc4e7e7576">
            {translate('NEXT_PERIODIC_REVIEW_DATE')}
          </TableHeadCell>
          <TableHeadCell id="0398adbe-8a8d-4804-af85-c7bde20e8bdc" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props) => {
        const row = props.row
        return (
          <TableRow {...props}>
            <td className="with-thumb clipped-cell">
              <div className="d-flex align-left">
                <div className="ellipsis" title={getFsiNameTooltip(row)}>
                  <ValidDocIndicator fsi={row} />
                  <DocIcon fsi={row} />
                  <a
                    href={buildUrlWithCSelections(`/show-file/${row.id}`)}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.button !== 0) return
                      e.preventDefault()
                      handleRowClick(row)
                    }}
                  >
                    {row.name}
                  </a>
                </div>
              </div>
            </td>
            <td className="narrow">
              {row?.workflow?.[0]?.id && row?.workflow?.[0]?.formattedId ? (
                <a
                  href={`#/workflows/${row.workflow[0].id}`}
                  onClick={(e) => {
                    e.preventDefault()
                    showModal(<WorkflowDetailsModal wfId={row.workflow[0].id} />)
                  }}
                >
                  {row.workflow[0].formattedId}
                </a>
              ) : (
                translate('NOT_AVAILABLE')
              )}
            </td>
            <td className="narrow updated">{formatDate(row.nextPeriodicReviewDate)}</td>
            <td className="actions">{row.valid && <TableMenu item={row} />}</td>
          </TableRow>
        )
      }}
    />
  )
}
